import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} text-center mb-8 md:mb-12`}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-sparkling-line text-[#1B3D2A]" />
          </div>
        </div>
        <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">{label}</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">{title}</h2>
      {subtitle && <p className="mt-3 text-sm text-[#5A6B5A]">{subtitle}</p>}
      <div className="w-12 h-1 bg-[#D4A574] mx-auto mt-3 rounded-full" />
    </div>
  );
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  const delayClass = delay === 0 ? '' : delay === 1 ? 'scroll-reveal-delay-1' : delay === 2 ? 'scroll-reveal-delay-2' : delay === 3 ? 'scroll-reveal-delay-3' : delay === 4 ? 'scroll-reveal-delay-4' : delay === 5 ? 'scroll-reveal-delay-5' : 'scroll-reveal-delay-6';
  return (
    <div ref={ref} className={`scroll-reveal ${delayClass} ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function HearingChronicDisease() {
  const navigate = useNavigate();

  const stats = [
    { num: '2.3x', label: '糖尿病者聽損風險', icon: 'ri-drop-line' },
    { num: '1.5x', label: '高血壓者聽損風險', icon: 'ri-heart-pulse-line' },
    { num: '40%', label: '聽損者具慢性病', icon: 'ri-stethoscope-line' },
    { num: '3.2x', label: '重度聽損認知退化風險', icon: 'ri-brain-line' },
  ];

  const researchFindings = [
    {
      icon: 'ri-drop-line',
      title: '糖尿病與聽損',
      tag: '風險增加 2.3 倍',
      desc: '多項大型流行病學研究證實，糖尿病患者發生聽力損失的風險是健康成年人的 2.3 倍。長期高血糖會損傷內耳微血管與神經纖維，導致漸進性感音神經性聽損。美國糖尿病學會（ADA）已將聽力篩檢納入糖友年度檢查建議項目。',
      image: 'https://readdy.ai/api/search-image?query=diabetes%20blood%20glucose%20test%20and%20hearing%20health%20concept%20modern%20medical%20illustration%20warm%20green%20tones%20clean%20background%20professional%20healthcare%20photography&width=600&height=400&seq=85&orientation=landscape',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: '高血壓與心血管',
      tag: '風險增加 1.5 倍',
      desc: '高血壓與動脈硬化會減少內耳血液灌注，加速聽力衰退。研究顯示，收縮壓每升高 10 mmHg，聽損風險增加 6%。內耳是人體中血液供應最精密的器官之一，對血流變化極為敏感。聽損也可能是心血管疾病的早期預警指標。',
      image: 'https://readdy.ai/api/search-image?query=heart%20health%20and%20blood%20pressure%20monitoring%20concept%20modern%20medical%20equipment%20warm%20green%20tones%20clean%20background%20professional%20healthcare%20photography%20wide%20angle&width=600&height=400&seq=86&orientation=landscape',
    },
    {
      icon: 'ri-brain-line',
      title: '認知退化與失智',
      tag: '風險增加 3.2 倍',
      desc: '約翰霍普金斯大學長期追蹤研究發現，重度聽損者認知退化速度比常人快 30–40%，失智風險增加 3.2 倍。當大腦長期缺乏聽覺刺激，語言處理區域會逐漸萎縮，形成「認知負荷」假說。早期配戴助聽器可顯著減緩認知退化進程。',
      image: 'https://readdy.ai/api/search-image?query=elderly%20person%20reading%20book%20with%20hearing%20aid%20in%20comfortable%20home%20environment%20warm%20lighting%20green%20tones%20cognitive%20health%20brain%20stimulation%20photography&width=600&height=400&seq=87&orientation=landscape',
    },
    {
      icon: 'ri-mental-health-line',
      title: '心理健康與憂鬱',
      tag: '風險增加 1.8 倍',
      desc: '聽損者因溝通困難常產生社交孤立，進而引發焦慮、憂鬱等心理問題。統計顯示，未配戴助聽器的聽損長者憂鬱症發生率是正常聽力者的 1.8 倍。助聽器不僅改善聽力，更能重建社交信心，提升整體生活品質與心理健康。',
      image: 'https://readdy.ai/api/search-image?query=elderly%20person%20smiling%20with%20family%20in%20warm%20living%20room%20social%20connection%20happy%20conversation%20hearing%20aid%20visible%20green%20tones%20photography&width=600&height=400&seq=88&orientation=landscape',
    },
  ];

  const riskFactors = [
    { icon: 'ri-restaurant-line', title: '飲食控制', desc: '低糖、低鹽、高纖飲食有助穩定血糖與血壓，保護內耳血管健康。' },
    { icon: 'ri-run-line', title: '規律運動', desc: '每週 150 分鐘中等強度運動可改善血液循環，降低聽損與慢性病風險。' },
    { icon: 'ri-volume-down-line', title: '噪音防護', desc: '避免長期暴露於 85 dB 以上噪音，使用耳塞或降噪耳機保護聽力。' },
    { icon: 'ri-calendar-check-line', title: '定期篩檢', desc: '40 歲以上建議每年進行聽力檢查，糖友與高血壓患者應每半年篩檢。' },
    { icon: 'ri-cup-line', title: '戒菸限酒', desc: '尼古丁與酒精會收縮血管、損傷聽神經，戒除可顯著降低聽損風險。' },
    { icon: 'ri-heart-pulse-line', title: '慢病管理', desc: '穩定控制血糖、血壓與血脂，是預防聽力退化的根本之道。' },
  ];

  const studies = [
    {
      year: '2024',
      journal: 'The Lancet',
      title: 'Hearing Loss and Dementia Risk in Older Adults',
      desc: '追蹤 8,000 名 65 歲以上長者長達 12 年，證實助聽器使用可將認知退化風險降低 48%。',
    },
    {
      year: '2023',
      journal: 'JAMA',
      title: 'Diabetes and Hearing Loss: A Meta-Analysis',
      desc: '統合分析 42 項研究，確認糖尿病與聽損存在顯著正相關，風險比為 2.31。',
    },
    {
      year: '2023',
      journal: 'Hypertension',
      title: 'Blood Pressure and Sensorineural Hearing Loss',
      desc: '發現收縮壓 > 140 mmHg 者，聽損發生率較正常血壓組高 42%，強調血壓控制的重要性。',
    },
    {
      year: '2022',
      journal: 'Ear and Hearing',
      title: 'Cochlear Microcirculation in Chronic Disease',
      desc: '利用影像技術證實，糖尿病與高血壓患者內耳毛細血管密度顯著降低，影響聽力功能。',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* ===== Hero ===== */}
        <div className="relative overflow-hidden text-white pt-4 md:pt-6 pb-8 md:pb-12">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E40] via-[#256F4A] to-[#1B3D2A]" />
          <div className="absolute -top-20 -right-20 w-72 h-72 md:w-96 md:h-96 rounded-full bg-[#D4A574]/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full bg-[#2E8B57]/20 blur-2xl" />
          <div className="absolute top-10 left-10 w-20 h-20 md:w-32 md:h-32 rounded-full bg-[#D4A574]/10 blur-2xl" />

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center pt-8 md:pt-12 pb-6 md:pb-8">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-3">
              Research & Studies
            </span>
            <h1 className="text-3xl md:text-5xl font-bold leading-tight">
              聽損與慢性病研究
            </h1>
            <p className="mt-4 text-white/80 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              聽力損失不僅是耳朵的問題，更與糖尿病、高血壓、心血管疾病及認知退化密切相關。
              了解最新醫學研究，及早預防、整合管理，守護聽力與全身健康。
            </p>
          </div>

          <svg className="absolute bottom-0 left-0 w-full h-auto block" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
          </svg>
        </div>

        {/* ===== Stats Bar ===== */}
        <div className="relative z-10 -mt-16 md:-mt-20 px-6 md:px-10 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl border border-[#E8E0D6] overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="grid grid-cols-2 md:grid-cols-4">
                {stats.map((stat, idx) => (
                  <div
                    key={stat.label}
                    className={`flex items-center gap-3 md:gap-4 p-5 md:p-7 ${idx !== 0 ? 'border-t md:border-t-0 md:border-l border-[#E8E0D6]' : ''}`}
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-[#1B3D2A]/10 flex items-center justify-center shrink-0">
                      <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                        <i className={`${stat.icon} text-lg md:text-xl text-[#1B3D2A]`} />
                      </div>
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-[#1B3D2A]">{stat.num}</div>
                      <div className="text-sm text-[#5A6B5A]">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== 研究發現 ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Research Findings" title="主要研究發現" subtitle="國際醫學研究證實，聽力損失與多種慢性疾病存在密切關聯" />

          <div className="space-y-6 md:space-y-8">
            {researchFindings.map((item, idx) => (
              <SectionReveal key={item.title} delay={idx % 2}>
                <div className={`flex flex-col lg:flex-row items-stretch gap-5 md:gap-6 ${idx % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className="lg:w-1/2 rounded-2xl overflow-hidden border border-[#E8E0D6] h-56 md:h-72 relative group">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-[#1B3D2A]/0 group-hover:bg-[#1B3D2A]/10 transition-colors duration-300" />
                  </div>
                  <div className="lg:w-1/2 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#1B3D2A]/10 flex items-center justify-center">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${item.icon} text-lg text-[#1B3D2A]`} />
                        </div>
                      </div>
                      <span className="text-xs font-bold text-[#D4A574] bg-[#D4A574]/10 px-3 py-1 rounded-full">
                        {item.tag}
                      </span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">{item.title}</h3>
                    <p className="mt-3 text-sm md:text-base text-[#5A6B5A] leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>

        {/* Wave: white to warm */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== 最新研究文獻 ===== */}
        <div className="bg-[#FAF8F5] py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionHeader label="Latest Studies" title="最新研究文獻" subtitle="彙整國際頂尖期刊發表的重要研究，掌握聽損與慢性病領域最新進展" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {studies.map((study, idx) => (
                <SectionReveal key={study.title} delay={idx}>
                  <div className="group bg-white rounded-2xl border border-[#E8E0D6] p-6 md:p-8 hover:border-[#D4A574]/50 hover:shadow-lg transition-all duration-300 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#1B3D2A] text-white px-2.5 py-1 rounded-full">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-calendar-line text-xs" />
                        </div>
                        {study.year}
                      </span>
                      <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">{study.journal}</span>
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-[#1B3D2A] leading-snug group-hover:text-[#D4A574] transition-colors">
                      {study.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                      {study.desc}
                    </p>
                    <div className="mt-4 flex items-center gap-1 text-sm text-[#D4A574] font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      閱讀摘要
                      <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave: warm to white */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== 風險因子與預防 ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Prevention" title="風險因子與預防建議" subtitle="從日常生活做起，降低聽損與慢性病共病的發生風險" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {riskFactors.map((item, idx) => (
              <SectionReveal key={item.title} delay={idx}>
                <div className="group bg-white rounded-2xl border border-[#E8E0D6] p-6 md:p-8 hover:border-[#D4A574]/50 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 text-center h-full">
                  <div className="w-14 h-14 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center mx-auto group-hover:bg-[#1B3D2A]/20 transition-colors duration-300">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className={`${item.icon} text-2xl text-[#1B3D2A]`} />
                    </div>
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-[#1B3D2A]">{item.title}</h3>
                  <p className="mt-2 text-sm text-[#5A6B5A] leading-relaxed">{item.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>

        {/* ===== 專家呼籲 ===== */}
        <div className="px-6 md:px-10 lg:px-16 pb-10 md:pb-14 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="bg-[#1B3D2A] rounded-2xl overflow-hidden p-6 md:p-8 flex flex-col md:flex-row items-center gap-5 md:gap-8">
              <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-stethoscope-line text-3xl text-white" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-white">專家呼籲：聽力檢查應納入慢性病管理</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">
                  國際聽力學會（ISA）建議，所有糖尿病、高血壓及心血管疾病患者應每年接受聽力篩檢，
                  並將聽力健康納入慢性病管理計畫。早期發現聽損，及早介入，可有效減緩認知退化與心理健康問題。
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/booking')}
                className="bg-white text-[#1B3D2A] px-6 py-3 rounded-xl text-sm font-bold hover:bg-[#FAF8F5] transition-colors whitespace-nowrap flex items-center gap-2 shrink-0"
              >
                <i className="ri-calendar-check-line" />
                預約聽力篩檢
              </button>
            </div>
          </SectionReveal>
        </div>

        {/* ===== CTA ===== */}
        <div className="px-6 md:px-10 lg:px-16 pb-10 md:pb-14 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="bg-[#FAF8F5] rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-[#1B3D2A]" />
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">持續學習</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">
                  想了解更多聽力保健知識？
                </h3>
                <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                  前往聽力知識專區，閱讀更多由專業聽力師撰寫的聽力保健文章，從助聽器選配到噪音防護，全面守護您的聽力健康。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/knowledge')}
                  className="bg-[#1B3D2A] text-white px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-book-open-line" />
                  </div>
                  聽力知識專區
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/booking')}
                  className="border border-[#E8E0D6] text-[#5A6B5A] px-6 md:px-8 py-3 rounded-xl text-sm font-semibold hover:border-[#1B3D2A] hover:text-[#1B3D2A] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-calendar-check-line" />
                  </div>
                  預約檢測
                </button>
              </div>
            </div>
          </SectionReveal>
        </div>
      </main>

      <Footer />
    </div>
  );
}